import { TrackingEvent } from '@marketplace-web/observability/event-tracker-data'

// Source: https://github.com/vinted/dwh-schema-registry/blob/5387d2ce7b44ee93711a15dfb97c60a4d984f22c/avro/events/common/shared/user.avdl#L25
type UserTarget =
  | 'confirm_everything_is_ok'
  | 'view_complaint'
  | 'save_address'
  | 'submit'
  | 'confirm_resolve_this_issue'
  | 'confirm_submit'
  | 'view_issue_details'
  | 'confirm'
  | 'contact_details_continue'
  | 'seller_add_contact_details'
  | 'shipping_discount_details'
  | 'add_to_report'
  | 'preview_report'
  | 'cancel_report'
  | 'estimated_refund_information'
  | 'item_discounts_refund_information'
  | 'bpf_refund_information'
  | 'more_information'
  | 'confirm_submit_request_return'
  | 'delete_contact_details'
  | 'confirm_phone_number_deletion'

type ClickEventArgs = {
  target: UserTarget
  screen?: string | null
  targetDetails?: string | null
  path?: string | null
  env?: string | null
}

type ClickEventExtra = {
  target: UserTarget
  screen?: string
  target_details?: string
  path?: string | null
  env?: string | null
}

export const clickEvent = (args: ClickEventArgs): TrackingEvent => {
  const { target, screen, targetDetails, env, path } = args
  const extra: ClickEventExtra = { target }

  if (screen) extra.screen = screen

  if (targetDetails) extra.target_details = targetDetails

  if (env) extra.env = env

  if (path) extra.path = path

  return {
    event: 'user.click',
    extra,
  }
}

// Source: https://github.com/vinted/dwh-schema-registry/blob/master/avro/events/user/view.avdl#L25
type ViewEventTarget = 'return_order'

type ViewEventArgs = {
  target: ViewEventTarget
  targetDetails?: string
  screen?: string
}

type ViewEventExtra = {
  target: ViewEventTarget
  target_details?: string
  screen?: string
}

export const viewEvent = (args: ViewEventArgs): TrackingEvent => {
  const { target, targetDetails, screen } = args

  const extra: ViewEventExtra = {
    target,
  }

  if (targetDetails) extra.target_details = targetDetails
  if (screen) extra.screen = screen

  return {
    event: 'user.view',
    extra,
  }
}

type ViewScreenEventArgs = {
  screen: string
}

export const viewScreenEvent = (args: ViewScreenEventArgs): TrackingEvent => {
  const { screen } = args

  const extra = {
    screen,
  }

  return {
    event: 'user.view_screen',
    extra,
  }
}

type CheckoutBuyerInputEventArgs = {
  transactionId?: number | null
  targetDetails?: string
  screen: string
  target: string
  valid: boolean | null
}

type CheckoutBuyerInputEventExtra = {
  valid: boolean | null
  screen: string
  target: string
  target_details?: string
  transaction_id?: number
}

export const checkoutBuyerInputEvent = (args: CheckoutBuyerInputEventArgs): TrackingEvent => {
  const { transactionId, targetDetails, screen, target, valid } = args

  const extra: CheckoutBuyerInputEventExtra = {
    screen,
    target,
    valid,
  }

  if (transactionId) extra.transaction_id = transactionId
  if (targetDetails) extra.target_details = targetDetails

  return {
    event: 'checkout.buyer.input',
    extra,
  }
}

type TransactionSellerInputEventArgs = {
  screen: string
  target: string
  valid: boolean | null
  targetDetails?: string
  transactionId?: number | null
}

type TransactionSellerInputEventExtra = {
  screen: string
  target: string
  valid: boolean | null
  target_details?: string
  transaction_id?: number
}

export const transactionSellerInputEvent = (
  args: TransactionSellerInputEventArgs,
): TrackingEvent => {
  const { transactionId, targetDetails, screen, target, valid } = args

  const extra: TransactionSellerInputEventExtra = {
    screen,
    target,
    valid,
  }

  if (transactionId) extra.transaction_id = transactionId
  if (targetDetails) extra.target_details = targetDetails

  return {
    event: 'transaction.seller_input',
    extra,
  }
}

type SelfServiceViewScreenEventArgs = {
  screen: string
  transactionId: number | string
  faqEntryId?: number | string | null
  details?: string | null
  variant?: string | null
}

type SelfServiceViewScreenEventExtra = {
  screen: string
  transaction_id: number | string
  faq_entry_id?: number | string | null
  details?: string | null
  // "variant" is not present in the schema, however it is used in some places
  variant?: string | null
}

export const selfServiceViewScreenEvent = (args: SelfServiceViewScreenEventArgs): TrackingEvent => {
  const { screen, transactionId, faqEntryId, details, variant } = args

  const extra: SelfServiceViewScreenEventExtra = {
    screen,
    transaction_id: transactionId,
  }

  if (faqEntryId) extra.faq_entry_id = faqEntryId
  if (details) extra.details = details
  if (variant) extra.variant = variant

  return {
    event: 'user.self_service.view_screen',
    extra,
  }
}

type SettingsUserInputEventArgs = {
  screen: string
  target: string
  isValid: boolean
  targetDetails?: string
}

type SettingsUserInputEventExtra = {
  screen: string
  target: string
  valid: boolean
  target_details?: string
}

export const settingsUserInputEvent = (args: SettingsUserInputEventArgs): TrackingEvent => {
  const { screen, target, isValid, targetDetails } = args

  const extra: SettingsUserInputEventExtra = {
    screen,
    target,
    valid: isValid,
    target_details: targetDetails,
  }

  return {
    event: 'settings.user.input',
    extra,
  }
}

type BuyerViewAddContactDetailsEventArgs = {
  transactionId: number
  screen: string
}

type BuyerViewAddContactDetailsEventExtra = {
  transaction_id: number
  screen: string
}

export const buyerViewAddContactDetailsEvent = (args: BuyerViewAddContactDetailsEventArgs) => {
  const { transactionId, screen } = args

  const extra: BuyerViewAddContactDetailsEventExtra = {
    transaction_id: transactionId,
    screen,
  }

  return {
    event: 'buyer.view_add_contact_details',
    extra,
  }
}

type SellerViewAddContactDetailsEventArgs = {
  transactionId: number
  screen: string
}

type SellerViewAddContactDetailsEventExtra = {
  transaction_id: number
  screen: string
}

export const sellerViewAddContactDetailsEvent = (args: SellerViewAddContactDetailsEventArgs) => {
  const { transactionId, screen } = args

  const extra: SellerViewAddContactDetailsEventExtra = {
    transaction_id: transactionId,
    screen,
  }

  return {
    event: 'seller.view_add_contact_details',
    extra,
  }
}

type BuyerPrecheckoutEventArgs = {
  transactionId: number
}

type BuyerPrecheckoutEventExtra = {
  transaction_id: number
}

export const buyerPrecheckoutEvent = (args: BuyerPrecheckoutEventArgs) => {
  const { transactionId } = args

  const extra: BuyerPrecheckoutEventExtra = {
    transaction_id: transactionId,
  }

  return {
    event: 'buyer.precheckout',
    extra,
  }
}

// Source: https://github.com/vinted/dwh-schema-registry/blob/e39aec4da4a00a8e70a8839adfbc164d04d10b91/avro/events/partial_refund_offers/click.avdl
type PartialRefundOffersClickTarget =
  | 'complaint_resolution'
  | 'offer_partial_refund'
  | 'partial_refund_offer_accept'
  | 'partial_refund_offer_decline'
  | 'partial_refund_offer_cancel'

type PartialRefundOffersClickEventArgs = {
  target: PartialRefundOffersClickTarget
  transactionId: string
  complaintId: string
  targetDetails?: string
}

type PartialRefundOffersClickEventExtra = {
  target: PartialRefundOffersClickTarget
  transaction_id: string
  complaint_id: string
  target_details?: string
}

export const partialRefundOffersClickEvent = (args: PartialRefundOffersClickEventArgs) => {
  const { target, transactionId, complaintId, targetDetails } = args

  const extra: PartialRefundOffersClickEventExtra = {
    target,
    transaction_id: transactionId,
    complaint_id: complaintId,
  }

  if (targetDetails) extra.target_details = targetDetails

  return { event: 'partial_refund_offers.click', extra }
}
